import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { FlowLogo } from '@/components/ui/FlowLogo';
import { CheckCircle } from 'lucide-react';

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number'
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export function ResetPassword() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      setError(null);
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.password,
      });

      if (updateError) throw updateError;
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <FlowLogo height={44} className="mx-auto mb-6" />
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Set new password</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">Enter your new password below</p>
        </div>

        <Card padding="lg">
          {success ? (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-success-50 ring-1 ring-inset ring-success-600/15 rounded-xl mb-4 dark:bg-success-900/30 dark:ring-success-400/20">
                <CheckCircle className="h-6 w-6 text-success-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Password updated!
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Your password has been successfully updated. Redirecting you to sign in...
              </p>
              <Link
                to="/login"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium dark:text-primary-400 dark:hover:text-primary-300"
              >
                Sign in now
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {error && (
                <div role="alert" className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700 dark:bg-danger-900/30 dark:border-danger-800 dark:text-danger-300">
                  {error}
                </div>
              )}

              <Input
                label="New password"
                type="password"
                autoComplete="new-password"
                placeholder="Enter your new password"
                error={errors.password?.message}
                hint="At least 8 characters with uppercase, lowercase, and number"
                {...register('password')}
              />

              <Input
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                placeholder="Confirm your new password"
                error={errors.confirmPassword?.message}
                {...register('confirmPassword')}
              />

              <Button type="submit" className="w-full" isLoading={isSubmitting}>
                Update password
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
